import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRegistration } from '../../context/RegistrationContext';

// Stránky registračného procesu
import WelcomePage from '../../pages/welcome/WelcomePage';
import PersonalInfoPage from '../../pages/personal-info/PersonalInfoPage';
import WorkshopSelectionPage from '../../pages/workshop-selection/WorkshopSelectionPage';
import ReservationPage from '../../pages/reservation/ReservationPage';
import RegistrationPage from '../../pages/registration/RegistrationPage';

const RegistrationFlow = () => {
  const { state, dispatch, actionTypes } = useRegistration();
  const { step, rezervacia, timeLeft, error } = state;

  // Kontrola platnosti rezervácie
  useEffect(() => {
    if (rezervacia && timeLeft <= 0 && (step === 4 || step === 5)) {
      dispatch({ type: actionTypes.SET_ERROR, payload: 'Vypršal čas na rezerváciu. Prosím, vyberte si dielňu znova.' });
      dispatch({ type: actionTypes.SET_REZERVACIA, payload: null });
      dispatch({ type: actionTypes.SET_STEP, payload: 3 });
    }
  }, [rezervacia, timeLeft, step, dispatch, actionTypes]);

  // Vyčistenie globálnej chyby pri zmene kroku
  useEffect(() => {
    dispatch({ type: actionTypes.RESET_ERROR });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step, dispatch, actionTypes]);

  // Indikátor postupu registrácie
  const renderProgressIndicator = () => {
    const steps = [
      { id: 1, label: 'Úvod' },
      { id: 2, label: 'Údaje' },
      { id: 3, label: 'Dielňa' },
      { id: 4, label: 'Rezervácia' },
      { id: 5, label: 'Registrácia' },
    ];

    return (
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center relative">
          {/* Spojovacia čiara */}
          <div className="absolute h-1 bg-gray-200 left-0 right-0 top-1/2 transform -translate-y-1/2 z-0"></div>
          
          {/* Kroky */}
          {steps.map((s) => (
            <div 
              key={s.id} 
              className="relative z-10 flex flex-col items-center"
            >
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                  s.id === step 
                    ? 'bg-accent animate-pulse' 
                    : s.id < step 
                      ? 'bg-primary' 
                      : 'bg-gray-300'
                }`}
              >
                {s.id < step ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  s.id
                )}
              </div>
              <span 
                className={`mt-1 text-xs ${
                  s.id === step 
                    ? 'text-accent font-medium' 
                    : s.id < step 
                      ? 'text-primary' 
                      : 'text-gray-500'
                }`}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Zobrazenie správnej stránky podľa aktuálneho kroku
  const renderCurrentStep = () => {
    switch (step) {
      case 1:
        return <WelcomePage />;
      case 2:
        return <PersonalInfoPage />;
      case 3:
        return <WorkshopSelectionPage />;
      case 4:
        return <ReservationPage />;
      case 5:
        return <RegistrationPage />;
      default:
        return <WelcomePage />;
    }
  };

  return (
    <div className="pb-8">
      {step > 1 && renderProgressIndicator()}

      {error && (
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-2 rounded">
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderCurrentStep()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default RegistrationFlow;
